import { SyntheticEvent, useCallback, useState } from 'react';

import { CountryCode, InputType } from 'src/constants';
import {
  getEmailError,
  getFirstNameError,
  getIsCountryPhoneValid,
  getIsEmailValid,
  getIsMessageValid,
  getIsTextValid,
  getIsYearValid,
  getLastNameError,
  getMessageError,
  getNameError,
  getPhoneError,
  getYearError,
} from 'src/utils';
import type { IForm } from 'src/interfaces';

const useForm = <T extends Record<string, any>>(
  initialFields: IForm,
  onFormSubmit: (obj: T) => void,
  onFormFailed?: (formFields: IForm) => void,
  extraValidator?: {
    fieldsToValidate: string[];
    isValid: (formFields: IForm) => boolean;
    setError: (formFields: IForm) => IForm;
    textError: string;
  }
) => {
  const [formFields, setFormFields] = useState(initialFields);
  const [formError, setFormError] = useState<string>('');

  const handleChange = useCallback(
    (fieldName: string) => (str: string) => {
      if (extraValidator && extraValidator.fieldsToValidate.includes(fieldName)) {
        if (formError) {
          setFormError('');

          setFormFields((prevFormFields) => ({
            ...prevFormFields,
            ...extraValidator.fieldsToValidate.reduce(
              (fields, name) => ({
                ...fields,
                [name]: {
                  ...prevFormFields[name],
                  isValid: true,
                },
              }),
              {}
            ),
          }));
        }
      }

      setFormFields((prevFormFields) => ({
        ...prevFormFields,
        [fieldName]: {
          ...prevFormFields[fieldName],
          isValid: true,
          textError: '',
          value: str,
        },
      }));
    },
    [extraValidator, formError]
  );

  const getIsFieldValid = useCallback(
    (fieldName: string) => {
      const field = formFields[fieldName];

      if (field.isRequired && !field.value.trim()) {
        return false;
      }

      switch (field.type) {
        case InputType.EMAIL:
          return getIsEmailValid(field.value);

        case InputType.FIRST_NAME:
          return getIsTextValid(field.value);

        case InputType.LAST_NAME:
          return getIsTextValid(field.value);

        case InputType.MESSAGE:
          return getIsMessageValid(field.value, field.isRequired);

        case InputType.NAME:
          return getIsTextValid(field.value);

        case InputType.PHONE:
          return getIsCountryPhoneValid(field.value, formFields.countryCode.value as CountryCode);

        case InputType.YEAR:
          return getIsYearValid(field.value);
      }

      return true;
    },
    [formFields]
  );

  const getInputError = useCallback(
    (fieldName: string) => {
      const field = formFields[fieldName];
      const value = field.value.trim();

      switch (field.type) {
        case InputType.EMAIL:
          return getEmailError(value);

        case InputType.PHONE:
          return getPhoneError(value);

        case InputType.NAME:
          return getNameError(value);

        case InputType.FIRST_NAME:
          return getFirstNameError(value);

        case InputType.LAST_NAME:
          return getLastNameError(value);

        case InputType.MESSAGE:
          return getMessageError(value);

        case InputType.YEAR:
          return getYearError(value);
      }

      return '';
    },
    [formFields]
  );

  const getIsFormValid = useCallback(
    () =>
      Object.keys(formFields).every(
        (fieldName) =>
          (!formFields[fieldName].isRequired && !formFields[fieldName].value.trim().length) ||
          getIsFieldValid(fieldName)
      ) &&
      (!extraValidator || extraValidator.isValid(formFields)),
    [extraValidator, formFields, getIsFieldValid]
  );

  const handleSubmit = useCallback(
    (evt: SyntheticEvent) => {
      evt.preventDefault();

      if (formError) {
        setFormError('');
      }

      if (getIsFormValid() && typeof onFormSubmit === 'function') {
        const values = Object.entries(formFields).reduce(
          (prev, [name, { value }]) => ({ ...prev, [name]: value.trim() }),
          {} as T
        );
        return onFormSubmit(values);
      }

      const setErrors = () => {
        let newFormFields: IForm = Object.entries(formFields).reduce((prevObj, [name, value]) => {
          const isValid = getIsFieldValid(name);
          const textError = isValid ? '' : getInputError(name);

          return {
            ...prevObj,
            [name]: { ...value, isValid, textError },
          };
        }, {});

        if (extraValidator) {
          newFormFields = extraValidator.setError(newFormFields);
          const extraValidatorIsValid = extraValidator.isValid(newFormFields);

          if (!extraValidatorIsValid) {
            setFormError(extraValidator.textError);
          }
        }

        setFormFields(newFormFields);
        onFormFailed?.(newFormFields);
      };

      setErrors();
    },
    [
      extraValidator,
      formError,
      formFields,
      getInputError,
      getIsFieldValid,
      getIsFormValid,
      onFormFailed,
      onFormSubmit,
    ]
  );

  return {
    formFields,
    formError,
    getIsFormValid,
    handleChange,
    handleSubmit,
  };
};

export default useForm;
